<!--suppress ALL -->
<template>
    <div class="dropdown" :class="rootClasses">
        <div
                v-if="!inline"
                role="button"
                ref="trigger"
                class="dropdown-trigger"
                @click="toggle">
            <slot name="trigger"/>
        </div>

        <transition name="fade">
            <div
                    v-if="isMobileModal"
                    v-show="isActive"
                    class="background"
            />
        </transition>
        <transition name="fade">
            <div
                    v-show="(!disabled && (isActive || hoverable)) || inline"
                    ref="dropdownMenu" class="dropdown-menu" :class="dropdownMenuClass">
                <div class="dropdown-content">
                    <slot/>
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
	import {Component, Vue, Prop, Watch} from 'annotation';

	@Component
	export default class GDropdown extends Vue {

		@Prop({default: null})
		public value: any;

		@Prop({default: false, type: Boolean})
		public disabled: Boolean;

		@Prop({default: false, type: Boolean})
		public hoverable: Boolean;

		@Prop({default: false, type: Boolean})
		public inline: Boolean;

		@Prop({default: '', type: String})
		public position: string;

		@Prop({default: '', type: String})
		public dropdownMenuClass: string;

		@Prop({type: Boolean, default: true})
		public mobileModal: any;

		public selected: any = this.value;

		public isActive: boolean = false;

		public _isDropdown: boolean = true; // Used internally by DropdownItem

		public get rootClasses() {
			return [`is-${this.position}`, {
				'is-disabled': this.disabled,
				'is-hoverable': this.hoverable,
				'is-inline': this.inline,
				'is-active': this.isActive || this.inline,
				'is-mobile-modal': this.isMobileModal
			}];
		}

		public get isMobileModal() {
			return this.mobileModal && !this.inline && !this.hoverable
		}

		@Watch('value')
		public onValueChange(value) {
			this.selected = value
		}

		/**
		 * Emit event when isActive value is changed.
		 */
		@Watch('isActive')
		public onIsActiveChange(value) {
			this.$emit('active-change', value)
		}

		/**
		 * Click listener from DropdownItem.
		 *   1. Set new selected item.
		 *   2. Emit input event to update the user v-model.
		 *   3. Close the dropdown.
		 */
		public selectItem(value) {
			if (this.selected !== value) {
				this.$emit('change', value)
				this.selected = value
			}
			this.$emit('input', value)
			this.isActive = false
		}

		/**
		 * White-listed items to not close when clicked.
		 */
		public isInWhiteList(el) {
			if (el === this.$refs.dropdownMenu) return true
			if (el === this.$refs.trigger) return true
			// All chidren from dropdown
			if (this.$refs.dropdownMenu !== undefined) {
				const children = this.$refs.dropdownMenu.querySelectorAll('*')
				for (const child of children) {
					if (el === child) {
						return true
					}
				}
			}
			// All children from trigger
			if (this.$refs.trigger !== undefined) {
				const children = this.$refs.trigger.querySelectorAll('*')
				for (const child of children) {
					if (el === child) {
						return true
					}
				}
			}

			return false
		}

		/**
		 * Close dropdown if clicked outside.
		 */
		public clickedOutside(event) {
			if (this.inline) return

			if (!this.isInWhiteList(event.target)) this.isActive = false
		}

		/**
		 * Toggle dropdown if it's not disabled.
		 */
		public toggle() {
			if (this.disabled || this.hoverable) return

			if (!this.isActive) {
				// if not active, toggle after clickOutside event
				// this fixes toggling programmatic
				this.$nextTick(() => {
					this.isActive = !this.isActive
				})
			} else {
				this.isActive = !this.isActive
			}
		}

		public activateDropdown() {
      if (!this.isActive) {
        this.$nextTick(() => {
          this.isActive = !this.isActive
        })
      }
    }

		public created() {
			if (typeof window !== 'undefined') {
				document.addEventListener('click', this.clickedOutside)
			}
		}

		public beforeDestroy() {
			if (typeof window !== 'undefined') {
				document.removeEventListener('click', this.clickedOutside)
			}
		}
	}
</script>

<style lang="scss" scoped>
    @import "../../../../style/variables_function";

    .dropdown {
        & + .dropdown {
            margin-left: 0.5em;
        }
        .background {
            @include overlay;
            position: fixed;
            background-color: rgba($black, 0.86);
            z-index: 10;
            cursor: pointer;
            @include desktop {
                display: none;
            }
        }
        .dropdown-menu {
            margin-top: 1rem;
            .dropdown-item {
                &.is-disabled {
                    cursor: not-allowed;
                    &:hover {
                        background: inherit;
                        color: inherit;
                    }
                }
            }
            .has-link a {
                @extend .dropdown-item;
                padding-right: 3rem;
                white-space: nowrap;
            }
        }

        &:not(.is-disabled) {
            .dropdown-menu {
                .dropdown-item {
                    &.is-disabled {
                        opacity: 0.5;
                    }
                }
            }
        }
        .navbar-item {
            height: 100%;
        }
        &.is-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            .dropdown-trigger {
                pointer-events: none;
            }
        }
        &.is-inline {
            .dropdown-menu {
                position: static;
                display: inline-block;
                padding: 0;
            }
        }
        &.is-top-right {
            .dropdown-menu {
                top: auto;
                bottom: 100%;
            }
        }
        &.is-top-left {
            .dropdown-menu {
                top: auto;
                bottom: 100%;
                right: 0;
                left: auto;
            }
        }
        &.is-bottom-left {
            .dropdown-menu {
                right: 0;
                left: auto;
            }
        }
        @include phone {
            &.is-mobile-modal {
                .dropdown-menu {
                    position: fixed;
                    width: calc(100vw - 40px);
                    max-width: 460px;
                    max-height: calc(100vh - 120px);
                    top: 25% !important;
                    left: 50% !important;
                    bottom: auto !important;
                    right: auto !important;
                    transform: translate3d(-50%, -25%, 0);
                    white-space: normal;
                    overflow-y: auto;
                    .dropdown-item {
                        padding: 1rem 1.5rem;
                    }
                }
            }
        }
    }
</style>
